import { useState } from 'react';
import { Send, CheckCircle, Mail, Phone, MapPin } from 'lucide-react';
import { images } from '../mock/data';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_VIDEO_URL = process.env.REACT_APP_LOGO_VIDEO_URL;
const API = `${BACKEND_URL}/api`;

export const Collaborate = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    location: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        
        // Reset after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            projectType: '',
            location: '',
            message: ''
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const featureTypes = [
    'Natural Water Body',
    'Flowing Water Form',
    'Habitat Environment',
    'Reflective Surface',
    'Integrated Landscape',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6 lg:px-12 bg-stone-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.projects[2]}
            alt="Collaborate"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light text-white mb-8 tracking-tight animate-fade-in-up">
            Let's Collaborate
          </h1>
          <p className="text-xl text-stone-300 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Every project begins with understanding place, land, and experience. Share your vision with us.
          </p>
        </div>
      </section>

      {/* Logo Animation */}
      <section className="py-12 px-6 lg:px-12 bg-white fade-in-section">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-sm overflow-hidden shadow-lg">
            <video
              className="w-full h-auto"
              autoPlay
              playsInline
            >
              <source src={LOGO_VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Side - Information */}
            <div>
              <h2 className="text-4xl font-light text-stone-800 mb-8 tracking-tight">
                Start a Conversation
              </h2>
              <div className="space-y-6 mb-12">
                <p className="text-lg text-stone-600 leading-relaxed">
                  Whether you're envisioning a reflective water surface, a flowing natural element, or a habitat-supporting environment—we'd love to explore how water can shape your landscape.
                </p>
                <p className="text-lg text-stone-600 leading-relaxed">
                  Our process begins with understanding your site, spatial intent, and how the water environment will integrate with place. Site-specific design, practical scale, realistic operation.
                </p>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-medium text-stone-800 mb-4">What to Expect</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-stone-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-stone-600">Initial consultation to understand site context and design intent</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-stone-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-stone-600">Site assessment—terrain, climate, spatial relationships</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-stone-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-stone-600">Site-responsive design proposal integrated with landscape</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-stone-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-stone-600">Collaborative refinement and long-term operation planning</span>
                    </li>
                  </ul>
                </div>

                <div className="p-8 bg-white rounded-sm shadow-md border border-stone-200">
                  <h3 className="text-xl font-medium text-stone-800 mb-4">Project Timeline</h3>
                  <p className="text-stone-600 leading-relaxed mb-4">
                    Most projects range from 4-12 weeks from design to completion, depending on scale, complexity, and site conditions.
                  </p>
                  <p className="text-sm text-stone-500">
                    We work with homeowners, architects, landscape designers, and hospitality projects.
                  </p>
                </div>

                {/* Direct Contact Info */}
                <div className="p-8 bg-stone-900 rounded-sm shadow-md">
                  <h3 className="text-xl font-medium text-white mb-6">Contact Us Directly</h3>
                  <div className="space-y-4">
                    <a href="mailto:pondlife@gmail.com" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors" data-testid="contact-email">
                      <Mail size={18} className="text-stone-500" />
                      <span>pondlife@gmail.com</span>
                    </a>
                    <a href="tel:+918679032311" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors" data-testid="contact-phone-india">
                      <Phone size={18} className="text-stone-500" />
                      <span>+91 86790 32311 (India)</span>
                    </a>
                    <a href="tel:+14088937953" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors" data-testid="contact-phone-us">
                      <Phone size={18} className="text-stone-500" />
                      <span>+1 408 893 7953 (US)</span>
                    </a>
                    <div className="flex items-center gap-3 text-stone-300">
                      <MapPin size={18} className="text-stone-500" />
                      <span>Serving India & United States</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white p-8 md:p-12 rounded-sm shadow-xl">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-600 text-sm">
                      {error}
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300"
                      placeholder="+91 XXX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block text-sm font-medium text-stone-700 mb-2">
                      Water Environment Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select feature type</option>
                      {featureTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-stone-700 mb-2">
                      Project Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300"
                      placeholder="City, State"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                      Tell Us About Your Vision *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell us about your site, landscape context, and what you envision for the water environment..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-stone-900 text-white px-8 py-4 rounded-sm hover:bg-stone-800 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send size={18} />}
                  </button>
                </form>
              ) : (
                <div className="text-center py-16 animate-fade-in">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-600" size={40} />
                  </div>
                  <h3 className="text-2xl font-light text-stone-800 mb-4">Message Sent!</h3>
                  <p className="text-stone-600">
                    Thank you for reaching out. We'll be in touch within 1-2 business days to discuss your water environment.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Image Banner */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {images.projects.slice(0, 3).map((image, index) => (
              <div
                key={index}
                className="relative h-80 rounded-sm overflow-hidden shadow-lg group"
              >
                <img
                  src={image}
                  alt={`Project ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
